// src/components/modules/ListingApp/services/listingApi.js
import axios from 'axios';

export const fetchListings = async (setRows) => {
  try {
    const response = await axios.get('/api/listings');
    setRows(response.data);
  } catch (error) {
    console.error('Error fetching listings:', error);
  }
};

export const updateListingFields = async (id, updates, onSuccess) => {
  if ('listing_price' in updates && updates.listing_price === '') return;

  const parsedPrice = parseFloat(updates?.listing_price);
  const updatePayload = {
    ...updates,
    listing_price: isNaN(parsedPrice) ? 0 : parsedPrice,
  };

  try {
    await axios.put(`/api/listings/${id}`, updatePayload);
    onSuccess?.();
  } catch (err) {
    console.error("Failed to update listing:", err);
    alert("Failed to update listing");
  }
};

export const updateListingFromModal = async (id, title, price, condition, listingFormat, listingDuration, onSuccess) => {
  try {
    await axios.put(`/api/listings/${id}`, {
      listing_title: title,
      listing_price: parseFloat(price),
      listing_condition: condition,
      listing_format: listingFormat,
      listing_duration: listingDuration,
    });
  } catch (err) {
    console.error("Failed to update listing via dialog:", err);
    alert("Update failed");
  } finally {
    onSuccess?.();
  }
};

// ---- Bulk ops (NEW) ----
export async function bulkList(ids = []) {
  const { data } = await axios.post('/api/listings/bulk/list', { ids });
  return data;
}

export async function bulkPriceIncrease(ids = [], percent = 0) {
  const { data } = await axios.post('/api/listings/bulk/price_increase', {
    ids,
    percent,
  });
  return data;
}

export async function bulkUpdateCondition(ids = [], condition = "") {
  const { data } = await axios.post('/api/listings/bulk/condition', {
    ids,
    condition,
  });
  return data;
}

/**
 * listOnEbay
 * - Default (no options): behaves as before, shows alerts for single LIST button
 * - With { silent: true }: no alerts, errors are thrown for caller to handle (LIST SELECTED flow)
 * - With { onStatusChange }: callback invoked on success so caller can update row status (e.g. "Listed")
 */
export const listOnEbay = async (id, options = {}) => {
  const { silent = false, onStatusChange } = options;
  try {
    const result = await publishListing(id);

    // Notify caller so the UI row can be updated to "Listed"
    if (typeof onStatusChange === 'function') {
      onStatusChange(id, 'Listed');
    }

    if (!silent) {
      alert(`Listing ${id} sent to eBay`);
    }
    return result;
  } catch (err) {
    console.error("Failed to publish listing:", err);
    if (!silent) {
      alert(`Failed to list ${id} on eBay: ${err.message}`);
    }
    // Always throw so bulk flows can track failures
    throw err;
  }
};

export const syncListing = async (id) => {
  try {
    await axios.post(`/api/listings/${id}/sync`);
    alert(`Listing ${id} synced successfully.`);
  } catch (err) {
    console.error("Failed to sync listing:", err);
    alert(`Failed to sync listing ${id}: ${err.message}`);
  }
};

export const endListing = async (id) => {
  try {
    await axios.post(`/api/listings/${id}/end`);
    alert(`Listing ${id} ended successfully.`);
  } catch (err) {
    console.error("Failed to end listing:", err);
    alert(`Failed to end listing ${id}: ${err.message}`);
  }
};

export const scrapeMetadata = async (cardName) => {
  try {
    const res = await axios.post('/api/listings/scrape_metadata', { card_name: cardName });
    alert(`Updated ${res.data.updated} listing(s) with metadata.`);
  } catch (err) {
    console.error("Failed to scrape metadata:", err);
    alert("Failed to scrape metadata");
  }
};

export const updatePriceOnly = async (id) => {
  const newPriceStr = prompt("Enter new price for eBay only:");
  if (!newPriceStr) return;

  const newPrice = parseFloat(newPriceStr);
  if (isNaN(newPrice)) {
    alert("Invalid price entered");
    return;
  }

  try {
    await axios.post(`/api/listings/${id}/update-price-only`, { price: newPrice });
    alert(`Listing ${id} price updated successfully on eBay.`);
  } catch (err) {
    console.error("Price-only update failed:", err);
    alert("Price-only update failed");
  }
};

// ---- Scrape predicted price (Listings) ----
export async function scrapeListingPrice(id) {
  const { data } = await axios.post(`/api/listings/${id}/scrape-price`);
  return data;
}

export async function applyLatestScrapedPrice(id) {
  const { data } = await axios.post(`/api/listings/${id}/apply-latest-scraped-price`);
  return data;
}


export async function syncListingsFromEbay({ limit = 25, filterMode = "stale_listed", listingIds = [] } = {}) {
  const { data } = await axios.post('/api/ebay-intelligence/listings/sync-from-ebay', {
    limit,
    filter_mode: filterMode,
    listing_ids: listingIds,
  });
  return data;
}

export const getListings = async () => {
  const res = await axios.get("/api/listings");
  return res.data.data || res.data;
};

export const publishListing = async (id) => {
  const response = await fetch(`/api/publish_ebay_listing/${id}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' }
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || `Failed with status ${response.status}`);
  }
  return data;
};

export const uploadListingImages = async (formData) => {
  const res = await fetch('/api/upload_listing_images', {
    method: 'POST',
    body: formData,
  });
  if (!res.ok) throw new Error(`Failed with status ${res.status}`);
  return res.json();
};

// NEW: fetch images for a listing (used by Batch Upload by PO image preview)
export const getListingImages = async (listingId) => {
  const res = await fetch(`/api/listings/${listingId}/images`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Failed to fetch listing images (${res.status}): ${text}`);
  }
  return res.json();
};

export const getListingById = async (id) => {
  try {
    const response = await axios.get(`/api/listings/${id}`);
    return response.data;
  } catch (err) {
    console.error('Error fetching listing by ID:', err);
    return null;
  }
};

export const updateListingImageOrder = async (listingId, imageUrls) => {
  const res = await fetch(`/api/listings/${listingId}/images/order`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ image_urls: imageUrls })
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Failed to update image order (${res.status}): ${text}`);
  }
  return res.json();
};

export const deleteListingImage = async (listingId, url) => {
  const res = await fetch(`/api/listings/${listingId}/images`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ url })
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Failed to delete image (${res.status}): ${text}`);
  }
  return res.json();
};

// === Print Label helpers (added by print workflow patch) ===
async function getLabelPrintersConfig() {
  const res = await axios.get('/api/label_printers');
  const printers = res?.data?.printers || [];
  const defaultKey = res?.data?.default_key || null;
  return { printers, defaultKey };
}

function pickDefaultPrinter(printers, defaultKey) {
  if (!Array.isArray(printers) || !printers.length) return null;
  if (defaultKey) {
    const hit = printers.find((p) => p?.key === defaultKey);
    if (hit) return hit;
  }
  return printers[0];
}

/**
 * Print a label for a listing.
 *
 * Primary path: same as Purchase Orders modal (POST direct to the configured print agent).
 * Fallback: backend proxy route /api/listings/<id>/print_label.
 */
export async function printLabel(listingId, opts = {}) {
  const id = Number(listingId);
  if (!id) throw new Error('Invalid listing id');

  try {
    const { printers, defaultKey } = await getLabelPrintersConfig();
    const printer = pickDefaultPrinter(printers, defaultKey);
    if (!printer?.target_url || !printer?.printer_name) {
      throw new Error('No printers configured');
    }

    const payload = {
      listing_id: id,
      card_name: opts.card_name || opts.cardName || '',
      card_number: opts.card_number || opts.cardNumber || '',
      printer: printer.printer_name,
      job_uid: `listing-${id}-${Date.now()}`,
    };

    await axios.post(printer.target_url, payload, { timeout: 20000 });
    return { ok: true, status: 'sent' };
  } catch (e) {
    // Fallback to backend proxy if the direct agent flow fails (keeps legacy behaviour working).
    const res = await fetch(`/api/listings/${id}/print_label`, { method: 'POST' });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(text || e?.message || `Print failed (${res.status})`);
    }
    try {
      return await res.json();
    } catch (_err) {
      return { ok: true, status: 'ok' };
    }
  }
}

export async function printLabelsSequential(listingIds, onProgress) {
  let ok = 0, fail = 0, errors = [];
  for (let i = 0; i < listingIds.length; i++) {
    const id = listingIds[i];
    try {
      await printLabel(id);
      ok++;
      onProgress && onProgress({ index: i + 1, total: listingIds.length, id, ok, fail });
    } catch (e) {
      fail++;
      errors.push({ id, error: e.message });
      onProgress && onProgress({ index: i + 1, total: listingIds.length, id, ok, fail, error: e.message });
    }
  }
  return { ok, fail, errors };
}

export async function reprintLabelById(listingId) {
  return printLabel(listingId);
}
// === End print helpers ===


export async function convertToAuction(listingId, { listing_duration, start_price }) {
  const res = await fetch(`/api/listings/${listingId}/convert-to-auction`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ listing_duration, start_price }),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || "Failed to convert listing to auction");
  }
  return data;
}

export const markListingAsTrainer = async (id, payload = {}) => {
  const { data } = await axios.post(`/api/listings/${id}/mark-trainer`, payload);
  return data;
};

export const fetchTrainerInventoryOptions = async () => {
  const { data } = await axios.get('/api/trainer_inventory');
  return Array.isArray(data) ? data : [];
};
